import React, { useState, useRef, useLayoutEffect } from 'react';
import { encode } from '@/base64';
import { newUid, deep_copy, decodeHtml } from '@/util';
import { useTranslation } from 'react-i18next';
import { IconButton } from '@teo/components';
import { Plus, Cross } from '@teo/components/icons';
import { QuestionSection } from '../QuestionSection';
import retry from 'async-await-retry';

export const initialTabbedContentBoxes = () => ({
  noSubmit: true,
  title: '',
  contentBoxes: [{ uid: newUid(20), title: 'Tab 1', text: '' }],
  type: 'TabbedContentBoxes',
});

const TabHeader = ({
  tab,
  contentBoxes,
  setContentBoxes,
  setActiveTab,
  activeTab,
}) => {
  const [tabText, setTabText] = useState(decodeHtml(tab?.title || ''));
  const inputRef = useRef(null);

  const resize = () => {
    const el = inputRef.current;
    if (!el) return;

    el.style.width = '0px';
    el.style.width = Math.min(el.scrollWidth + 8, 200) + 'px';
  };

  const handleBlur = () => {
    setContentBoxes((prev) =>
      prev.map((el) =>
        el?.uid == tab?.uid ? { ...el, title: decodeHtml(tabText) } : el
      )
    );
  };

  useLayoutEffect(() => {
    setTabText(decodeHtml(tab?.title || ''));
    resize();
  }, [tab]);

  return (
    <div
      onClick={() => setActiveTab(tab)}
      className={`flex cursor-pointer items-center gap-1 rounded-lg px-3 py-[6px] ${
        activeTab?.uid == tab?.uid ? 'bg-grey-02' : 'bg-white'
      }`}
    >
      <p
        className={`text-base font-medium ${
          activeTab?.uid == tab?.uid ? 'tex-grey-07' : 'text-black'
        }`}
      >
        <input
          ref={inputRef}
          type="text"
          id={'uid_' + tab?.uid}
          value={tabText}
          onInput={resize}
          style={{ maxWidth: 200 }}
          onChange={({ target: { value } }) => {
            setTabText(value);
          }}
          onBlur={handleBlur}
          className={`border-0 border-b border-transparent bg-grey-02 p-1 !shadow-none !ring-0 focus:border-0 focus:border-b focus:border-white 
            ${activeTab?.uid == tab?.uid ? 'bg-grey-02' : 'bg-white'}`}
        />
      </p>
      <IconButton
        className="!text-grey-07"
        size="sm"
        Icon={Cross}
        variant="ghost"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          const newContentBoxesList = contentBoxes?.filter(
            (el) => el?.uid != tab?.uid
          );
          setContentBoxes(newContentBoxesList);
          if (tab?.uid == activeTab?.uid) {
            setActiveTab(
              newContentBoxesList?.length ? newContentBoxesList[0] : null
            );
          }
        }}
      />
    </div>
  );
};

const TabContent = ({ state, contentBoxes, setContentBoxes, activeTab }) => {
  const handleChangeText = () => {
    let answerNode = document.querySelector(
      `#${CSS.escape(state.uid)} .wg-tab-${activeTab?.uid} .question_optional`
    );

    setContentBoxes((prev) =>
      prev.map((el, i) =>
        el?.uid == activeTab?.uid ? { ...el, text: answerNode.innerHTML } : el
      )
    );
  };

  return (
    <div
      className={`wg-tab-${activeTab?.uid} bg-grey-0 rounded-lg border px-4 py-3 `}
      onBlur={() => handleChangeText()}
    >
      <QuestionSection questionVraag={activeTab?.text} tabContent={true} />
    </div>
  );
};

export const TabbedContentBoxEdit = ({
  state,
  index,
  onModified = undefined,
  setStateFn = undefined,
}) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState(state.title);
  const [contentBoxes, setContentBoxes] = useState(
    (state.contentBoxes || []).map((item) => ({
      ...item,
      title: decodeHtml(item.title || ''),
    }))
  );

  const [activeTab, setActiveTab] = useState(
    state?.contentBoxes?.length ? state?.contentBoxes[0] : null
  );

  //   //generate the state
  const getState = async () => {
    let newState = deep_copy(state);
    await retry(() => {
      let questionNode = document.querySelector(
        `#${CSS.escape(state.uid)} .wg-question .question_optional`
      );
      if (questionNode) {
        newState.title = questionNode.innerHTML;
        return Promise.resolve();
      }
      return Promise.reject();
    });
    newState.contentBoxes = contentBoxes;
    return newState;
  };
  setStateFn && setStateFn(getState);

  const handleAddItem = () => {
    if (contentBoxes?.length) {
      const newItem = {
        uid: newUid(20),
        title: `Tab ${contentBoxes.length + 1}`,
        text: '',
      };
      setContentBoxes((prev) => [...prev, newItem]);
    } else {
      const newItem = { uid: newUid(20), title: `Tab 1`, text: '' };
      setContentBoxes((prev) => [...prev, newItem]);
    }

    setTimeout(() => {
      let placeholder = document.getElementById(state.uid);
      const labelElement = placeholder.querySelector(
        `[for=uid_${state.uid}_${contentBoxes?.length}]`
      );
      if (labelElement) {
        labelElement.click();
      }
    }, 0);
  };

  return (
    <div
      data-open="SOW"
      className={`flex w-full flex-col gap-2 lg:p-2`}
      style={{ maxWidth: 'calc(100vw - 32px)' }}
      data-state={encode(JSON.stringify(state))}
      id={state.uid}
    >
      <div className="wg-question">
        <QuestionSection questionVraag={title} title={'test'} />
      </div>

      <div className="flex w-full flex-col gap-3">
        <div className="flex flex-wrap items-center gap-3 ">
          {contentBoxes?.map((tab) => {
            return (
              <TabHeader
                key={tab?.uid}
                tab={tab}
                contentBoxes={contentBoxes}
                setContentBoxes={setContentBoxes}
                setActiveTab={setActiveTab}
                activeTab={activeTab}
              />
            );
          })}

          <IconButton
            Icon={Plus}
            variant="outline"
            onClick={() => handleAddItem()}
          />
        </div>
        <div>
          <TabContent
            key={activeTab?.uid}
            state={state}
            contentBoxes={contentBoxes}
            setContentBoxes={setContentBoxes}
            activeTab={activeTab}
          />
        </div>
      </div>

      <div data-closed="EOW"></div>
    </div>
  );
};
